import { ConnectError, type PromiseClient } from "@connectrpc/connect";
import { captureException } from "@sentry/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FundSource } from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { WalletService } from "schema/gen/es/chiikipoint/wallet/v2/service_connect";
import Cancel from "~icons/material-symbols/cancel-outline";
import CheckCircle from "~icons/material-symbols/check-circle-outline";
import { css } from "../../../../styled-system/css";
import { LoadingIcon } from "../../../components/loadingIcon";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";
import { formatNumber } from "../../../libs/utils";

type DistributionCodeModalProps = {
  data: DistributionCodeAcceptedModalStateData;
  client: PromiseClient<typeof WalletService>;
  onClose: () => void;
};

export function DistributionCodeModal({
  data: { distributionCode },
  client,
  onClose,
}: DistributionCodeModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [codeDetail, setCodeDetail] = useState<{
    amount: bigint;
    fundSource: FundSource;
  } | null>(null);

  // 配布コードの詳細を取得
  useEffect(() => {
    const fetchCodeDetail = async () => {
      try {
        const response = await client.getDistributionCodeDetail({
          distributionCode,
        });
        if (!response.fundSource) {
          throw new Error("No fund source found");
        }
        setCodeDetail({
          amount: response.amount,
          fundSource: response.fundSource,
        });
      } catch (error) {
        const appError = new AppError(
          ERROR_CODES.CONNECT_GET_DISTRIBUTION_CODE_DETAIL_FAILED,
          error instanceof Error ? error.message : "Unknown error",
          { originalError: error },
        );
        captureException(appError);
        setIsError(true);
      }
    };
    fetchCodeDetail();
  }, [client, distributionCode]);

  const handleAccept = useCallback(async () => {
    setIsLoading(true);

    try {
      await client.consumeDistributionCode({
        distributionCode,
      });
      setIsAccepted(true);
    } catch (error) {
      if (error instanceof ConnectError) {
        // 配布コードがすでに使用されている場合は captureException しない
        if (error.message === "[failed_precondition] failed_precondition") {
          setIsError(true);
          return;
        }
      }

      const appError = new AppError(
        ERROR_CODES.CONNECT_CONSUME_DISTRIBUTION_CODE_FAILED,
        error instanceof Error ? error.message : "Unknown error",
        { originalError: error },
      );
      captureException(appError);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [client, distributionCode]);

  const ErrorComponent = useMemo(() => {
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("配布コードが利用できません")}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "text.accentError",
            })}
          >
            <Cancel />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onClose}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [onClose]);

  const Accepted = useMemo(() => {
    return (
      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "80%",
          maxWidth: "360px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        })}
      >
        <h2
          className={css({
            fontSize: "18px",
            fontWeight: "bold",
            marginBottom: "24px",
          })}
        >
          {t("ポイント受け取り完了")}
        </h2>

        <div
          className={css({
            borderRadius: "8px",
            marginBottom: "24px",
          })}
        >
          <div
            className={css({
              fontSize: "56px",
              fontWeight: "bold",
              color: "text.accentSuccess",
            })}
          >
            <CheckCircle />
          </div>
        </div>

        <button
          type="button"
          className={css({
            padding: "12px",
            borderRadius: "8px",
            width: "100%",
            fontWeight: "bold",
            cursor: "pointer",
            backgroundColor: "surface.secondary",
            color: "text.primary",
            border: "1px solid",
            borderColor: "border.secondary",
          })}
          onClick={onClose}
        >
          {t("閉じる")}
        </button>
      </div>
    );
  }, [onClose]);

  return (
    <div
      className={css({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(226, 226, 226, 0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      })}
    >
      {isError ? (
        ErrorComponent
      ) : isAccepted ? (
        Accepted
      ) : (
        <div
          className={css({
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "24px",
            width: "80%",
            maxWidth: "360px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          })}
        >
          <h2
            className={css({
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "24px",
            })}
          >
            {t("ポイントを受け取りますか？")}
          </h2>

          {codeDetail ? (
            <>
              <div
                className={css({
                  width: "100%",
                  marginBottom: "16px",
                })}
              >
                <div
                  className={css({
                    backgroundColor: "surface.accentSuccessLight",
                    padding: "16px",
                    borderRadius: "8px",
                    textAlign: "center",
                  })}
                >
                  <div
                    className={css({
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "text.secondary",
                      marginBottom: "4px",
                    })}
                  >
                    {codeDetail.fundSource.name}
                  </div>
                  <div
                    className={css({
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "center",
                      gap: "4px",
                    })}
                  >
                    <span
                      className={css({
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: "text.accentSuccess",
                      })}
                    >
                      {formatNumber(Number(codeDetail.amount))}
                    </span>
                    <span
                      className={css({
                        fontSize: "18px",
                        fontWeight: "normal",
                        color: "text.secondary",
                      })}
                    >
                      pt
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className={css({
                  backgroundColor: isLoading
                    ? "surface.secondary"
                    : "text.accentPrimary",
                  color: "white",
                  padding: "12px",
                  borderRadius: "8px",
                  width: "100%",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "8px",
                })}
                onClick={handleAccept}
                disabled={isLoading}
              >
                {isLoading ? <LoadingIcon /> : t("受け取る")}
              </button>

              <button
                type="button"
                className={css({
                  padding: "12px",
                  borderRadius: "8px",
                  width: "100%",
                  fontWeight: "bold",
                  cursor: "pointer",
                  opacity: isLoading ? 0.5 : 1,
                  color: "text.primary",
                })}
                disabled={isLoading}
                onClick={onClose}
              >
                {t("キャンセル")}
              </button>
            </>
          ) : (
            <div
              className={css({
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "120px",
              })}
            >
              <LoadingIcon />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
